/**
 * Meta Variants - Product Group Navigation System
 * Allows navigation between completely different products based on shared attributes
 */
class MetaVariants extends HTMLElement {
  constructor() {
    super();
    
    // Parse product group data from embedded JSON
    this.productGroup = JSON.parse(this.querySelector('.meta-info').textContent);
    this.currentProductId = this.dataset.product;
    
    // Find current product in the group
    this.currentProduct = this.productGroup.products.find(p => p.id == this.currentProductId);
    
    // Only show if there are multiple products to choose from
    if (this.productGroup.products.length < 2) {
      this.remove();
      return;
    }
    
    // Cache DOM elements
    this.variantLabels = this.querySelectorAll('.label-swatch.meta-variant');
    this.variantInputs = this.querySelectorAll('input[type="radio"]');
    this.loadingElement = this.querySelector('.meta-variants__loading');
    
    // Set up variant navigation based on type
    if (this.dataset.single === 'true') {
      this.initializeSingleOption();
    } else {
      this.initializeMultiOption();
    }
    
    // Add event listeners
    this.addEventListeners();
  }

  /**
   * Initialize for single option variants (direct product list)
   */
  initializeSingleOption() {
    this.variantLabels.forEach(label => {
      if (label.dataset.handle === this.currentProduct.handle) {
        label.classList.add('active');
        const input = label.querySelector('input');
        if (input) input.checked = true;
      }
    });
    
    // Add part numbers if available
    if (this.currentProduct.partNumber) {
      this.addPartNumbers();
    }
  }

  /**
   * Initialize for multi-option variants (size, color, etc.)
   */
  initializeMultiOption() {
    // Set current selections
    this.currentProduct.options.forEach((option, index) => {
      const label = this.querySelectorAll('.meta-variants__label__selected')[index];
      if (label) {
        label.textContent = option;
      }
      
      // Mark active labels and check inputs
      this.variantLabels.forEach(label => {
        if (label.dataset.option === option) {
          label.classList.add('active');
          const input = label.querySelector('input');
          if (input) input.checked = true;
        }
      });
    });
    
    // Sort options naturally
    this.sortVariantOptions();
    
    // Check availability
    this.checkAvailability();
    
    // Set up part numbers if needed
    if (this.productGroup.products[0].partNumber && this.getDuplicates().length > 1) {
      this.renderPartNumbers(this.getDuplicates());
    }
  }

  /**
   * Natural sort for alphanumeric strings
   */
  naturalSort(a, b) {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return collator.compare(a, b);
  }

  /**
   * Sort variant options in each category
   */
  sortVariantOptions() {
    const containers = this.querySelectorAll('.meta-variants__container[data-category]');
    containers.forEach(container => {
      const items = Array.from(container.querySelectorAll('li'));
      
      items.sort((a, b) => {
        const labelA = a.querySelector('.meta-variant');
        const labelB = b.querySelector('.meta-variant');
        return this.naturalSort(labelA?.dataset.option || '', labelB?.dataset.option || '');
      });
      
      items.forEach(item => container.appendChild(item));
    });
  }

  /**
   * Add part numbers to single option variants
   */
  addPartNumbers() {
    this.variantLabels.forEach(label => {
      const product = this.productGroup.products.find(p => p.handle === label.dataset.handle);
      if (product && product.partNumber) {
        label.innerHTML = label.innerHTML + ` | ${product.partNumber}`;
      }
    });
  }

  /**
   * Get products with identical option combinations
   */
  getDuplicates() {
    const currentOptions = this.currentProduct.options.join(',');
    return this.productGroup.products.filter(product => 
      product.options.join(',') === currentOptions
    );
  }

  /**
   * Render part number selector for duplicate options
   */
  renderPartNumbers(duplicates) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'js product-form__input variant-input-wrapper relative';
    
    const legend = document.createElement('legend');
    legend.className = 'sr-only';
    legend.textContent = 'Part Number';
    fieldset.appendChild(legend);
    
    const labelDiv = document.createElement('div');
    labelDiv.className = 'form__label flex items-center justify-between gap-2 w-full';
    labelDiv.innerHTML = '<div class="flex gap-2">Part Number: <span class="font-medium meta-variants__label__selected"></span></div>';
    fieldset.appendChild(labelDiv);
    
    const container = document.createElement('ul');
    container.className = `swatches swatches--${this.getAttribute('data-rounded-swatch') || 'rounded'} flex items-start flex-wrap gap-4 meta-variants__container`;
    
    duplicates.forEach(product => {
      const li = document.createElement('li');
      
      const label = document.createElement('label');
      label.className = 'label-swatch meta-variant meta-variant__part-number inline-block text-sm font-medium leading-none cursor-pointer relative';
      label.dataset.productId = product.id;
      label.dataset.handle = product.handle;
      
      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'sr-only';
      input.name = 'meta-part-number';
      input.value = product.partNumber;
      
      if (product.id == this.currentProductId) {
        label.classList.add('active');
        input.checked = true;
        labelDiv.querySelector('.meta-variants__label__selected').textContent = product.partNumber;
      }
      
      label.appendChild(input);
      label.appendChild(document.createTextNode(product.partNumber));
      li.appendChild(label);
      container.appendChild(li);
    });
    
    fieldset.appendChild(container);
    this.appendChild(fieldset);
    
    // Add event listeners to new part number inputs
    container.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', (e) => {
        const label = e.target.closest('.meta-variant__part-number');
        if (label) this.handlePartNumberClick(label);
      });
    });
  }

  /**
   * Check availability of option combinations
   */
  checkAvailability() {
    // Get all active selections
    const activeOptions = Array.from(this.querySelectorAll('.label-swatch.meta-variant.active'))
      .map(label => label.dataset.option);
    
    // Build set of valid combinations
    const validCombinations = new Set(
      this.productGroup.products.map(p => p.options.join(','))
    );
    
    // Check each option label
    const containers = this.querySelectorAll('.meta-variants__container[data-category]');
    containers.forEach((container, categoryIndex) => {
      container.querySelectorAll('.label-swatch.meta-variant').forEach(label => {
        if (label.classList.contains('active')) return;
        
        // Test if this option would create a valid combination
        const testOptions = [...activeOptions];
        testOptions[categoryIndex] = label.dataset.option;
        
        if (!validCombinations.has(testOptions.join(','))) {
          label.classList.add('unavailable');
        } else {
          label.classList.remove('unavailable');
        }
      });
    });
  }

  /**
   * Add event listeners to all interactive elements
   */
  addEventListeners() {
    // Listen for input changes instead of label clicks
    this.variantInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const label = e.target.closest('.label-swatch.meta-variant');
        if (!label) return;
        
        if (label.dataset.handle) {
          // Direct product selection
          this.navigateToProduct(label.dataset.handle);
        } else if (label.dataset.option) {
          // Option-based selection
          this.handleOptionClick(label);
        }
      });
    });
  }

  /**
   * Handle part number label clicks
   */
  handlePartNumberClick(label) {
    if (label.classList.contains('active')) return;
    this.navigateToProduct(label.dataset.handle);
  }

  /**
   * Handle option label clicks
   */
  handleOptionClick(label) {
    const isUnavailable = label.classList.contains('unavailable');
    const changedOption = label.dataset.option;
    
    if (isUnavailable) {
      // For unavailable options, find ANY product with this option
      const availableProduct = this.productGroup.products.find(product => 
        product.options.includes(changedOption)
      );
      
      if (availableProduct) {
        this.navigateToProduct(availableProduct.handle);
      }
    } else {
      // Normal flow for available options
      // Update active state in this category
      const container = label.closest('.meta-variants__container');
      container.querySelector('.active')?.classList.remove('active');
      label.classList.add('active');
      
      // Find matching product
      const matchingProduct = this.findMatchingProduct(changedOption);
      
      if (matchingProduct) {
        this.navigateToProduct(matchingProduct.handle);
      }
    }
  }

  /**
   * Find the best matching product for the current selections
   */
  findMatchingProduct(changedOption) {
    // Get all currently selected options
    const selectedOptions = Array.from(this.querySelectorAll('.label-swatch.meta-variant.active'))
      .map(label => label.dataset.option)
      .filter(Boolean);
    
    // Find products that have the changed option
    const candidateProducts = this.productGroup.products.filter(product => 
      product.options.includes(changedOption)
    );
    
    // Score each candidate by how many selected options it matches
    let bestMatch = null;
    let bestScore = -1;
    
    candidateProducts.forEach(product => {
      const score = selectedOptions.filter(option => 
        product.options.includes(option)
      ).length;
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = product;
      }
    });
    
    return bestMatch;
  }

  /**
   * Navigate to a different product in the group
   */
  navigateToProduct(productHandle) {
    const productUrl = `/products/${productHandle}`;
    
    // Show loading state briefly
    this.showLoading();
    
    // Simply navigate to the new product page
    window.location.href = productUrl;
  }

  /**
   * Show loading overlay
   */
  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.classList.remove('hidden');
    }
  }

  /**
   * Hide loading overlay
   */
  hideLoading() {
    if (this.loadingElement) {
      this.loadingElement.classList.add('hidden');
    }
  }
}

// Register the custom element
customElements.define('meta-variants', MetaVariants);